import {
  directoryRegions,
  type DirectoryRegion,
  type DirectorySpecialty,
} from "../data/directory-pages.ts";
import type { ProviderRow } from "@/lib/public-content.types";

export function normalizeDirectoryText(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const cantonAliases: Record<string, string[]> = {
  bl: ["Baselland"],
  bs: ["Basel Stadt"],
  fr: ["Fribourg"],
  ge: ["Geneva", "Genève"],
  gr: ["Grisons", "Grigioni"],
  ju: ["Jura"],
  lu: ["Lucerne"],
  ne: ["Neuchâtel"],
  sg: ["Sankt Gallen"],
  ti: ["Ticino"],
  vd: ["Vaud"],
  vs: ["Valais"],
  zh: ["Zuerich", "Zurich"],
};

function containsPhrase(text: string, phrase: string) {
  return ` ${text} `.includes(` ${normalizeDirectoryText(phrase)} `);
}

export function providerCantonIds(provider: Pick<ProviderRow, "canton" | "location">): string[] {
  const findCantons = (value: string) => {
    const text = normalizeDirectoryText(value);
    return directoryRegions
      .filter((region) =>
        [region.canton, region.name, ...(cantonAliases[region.canton] ?? [])].some((alias) =>
          containsPhrase(text, alias),
        ),
      )
      .map((region) => region.canton);
  };
  // An explicit canton takes precedence over ambiguous town names (e.g. Basel).
  const explicit = findCantons(provider.canton ?? "");
  if (explicit.length) return explicit;
  const locationCantons = findCantons(provider.location);
  if (locationCantons.length) return locationCantons;
  const location = normalizeDirectoryText(provider.location);
  return directoryRegions
    .filter((region) => region.locations.some((town) => containsPhrase(location, town)))
    .map((region) => region.canton);
}

export function providerMatchesSpecialty(
  provider: Pick<ProviderRow, "category" | "services">,
  specialty: DirectorySpecialty,
) {
  const text = normalizeDirectoryText([provider.category, ...(provider.services ?? [])].join(" "));
  return specialty.terms.some((term) => text.includes(normalizeDirectoryText(term)));
}

export function filterDirectoryProviders(
  providers: ProviderRow[],
  region?: DirectoryRegion,
  specialty?: DirectorySpecialty,
) {
  return providers.filter(
    (provider) =>
      (!region || providerCantonIds(provider).includes(region.canton)) &&
      (!specialty || providerMatchesSpecialty(provider, specialty)),
  );
}

// Only explicit, published area labels qualify. Do not infer an operational
// area from an office address, a description, or a company name.
export function providerDeclaredServiceArea(
  provider: Pick<ProviderRow, "canton">,
): "Deutschschweiz" | undefined {
  return normalizeDirectoryText(provider.canton ?? "") === "deutschschweiz"
    ? "Deutschschweiz"
    : undefined;
}

// Bern, Freiburg, Wallis and Graubünden contain German-speaking regions;
// the UI explicitly asks visitors to confirm their municipality and language.
const germanSpeakingCantonIds = new Set([
  "ag",
  "ai",
  "ar",
  "be",
  "bl",
  "bs",
  "fr",
  "gl",
  "gr",
  "lu",
  "nw",
  "ow",
  "sg",
  "sh",
  "so",
  "sz",
  "tg",
  "ur",
  "vs",
  "zg",
  "zh",
]);

export function filterServiceAreaProviders(providers: ProviderRow[], region: DirectoryRegion) {
  if (!germanSpeakingCantonIds.has(region.canton)) return [];
  return providers.filter(
    (provider) =>
      providerDeclaredServiceArea(provider) === "Deutschschweiz" &&
      !providerCantonIds(provider).includes(region.canton),
  );
}

export function providerWebsite(value: string | null): string | undefined {
  if (!value?.trim()) return undefined;
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`);
    return ["https:", "http:"].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}
